import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Crown, Award, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { gamificationService } from '../services';
import PageContainer from '../components/PageContainer';
import EmptyState from '../components/EmptyState';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/Loading';
import {getClassTypeCollor, getClassTypeIcon} from "../utils/characterTranslation";

const Ranking = () => {
  const { user } = useAuth();
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    loadRanking();
  }, [pagination.page]);

  const loadRanking = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await gamificationService.getRanking({
        page: pagination.page,
        limit: pagination.limit,
      });

      setRanking(response.ranking || []);
      setPagination(response.pagination || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar ranking');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getRankIcon = (position) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return '🏅';
  };

  if (loading && ranking.length === 0) {
    return (
      <PageContainer title="Ranking do Clã" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner text="Carregando ranking..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      icon={Trophy}
      title="Ranking do Clã"
      description="Os melhores personagens do clã por pontuação total"
    >

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 text-red-400 px-6 py-4 rounded-xl mb-6"
        >
          <div className="flex items-center justify-between">
            <p className="font-medium">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={loadRanking}
            >
              Tentar novamente
            </Button>
          </div>
        </motion.div>
      )}

      {/* Ranking List */}
      {ranking.length > 0 ? (
        <>
          {/* Top 3 - Podium */}
          {pagination.page === 1 && ranking.slice(0, 3).length > 0 && (
            <Card variant="glass" className="mb-8">
              <div className="p-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Crown className="w-6 h-6 text-amber-400" />
                  Pódio dos Campeões
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {ranking.slice(0, 3).map((character, index) => (
                    <motion.div
                      key={character.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="relative p-6 rounded-xl text-center transform hover:scale-105 transition-all duration-200"
                      style={{
                        background: (index + 1) === 1
                          ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)'
                          : (index + 1) === 2
                          ? 'linear-gradient(135deg, rgba(156, 163, 175, 0.2) 0%, rgba(107, 114, 128, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(234, 88, 12, 0.1) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <div className="text-5xl mb-3">{getRankIcon(index+1)}</div>
                      <div className="text-3xl font-bold text-white mb-2">#{index+1}</div>
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <span className="text-2xl">{getClassTypeIcon(character.class)}</span>
                        <span className="font-bold text-white text-lg">{character.name}</span>
                      </div>
                      <span className={`inline-block px-3 py-1 rounded-lg text-xs font-medium mb-3 ${getClassTypeCollor(character.class)}`}>
                        {character.class} Nv.{character.level}
                      </span>
                      <div className="mt-4">
                        <span className="text-3xl font-bold text-amber-400">
                          {character.totalPoints.toLocaleString()}
                        </span>
                        <div className="text-xs text-gray-400 mt-1">pontos</div>
                      </div>
                      <div className="mt-3 text-xs text-gray-500">
                        por {character.user.name}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Full Ranking Table */}
          <Card variant="glass">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Award className="w-6 h-6 text-cyan-400" />
                  Ranking Completo
                </h2>
                <span className="px-3 py-1 rounded-lg bg-cyan-500/20 text-cyan-400 text-sm font-medium border border-cyan-500/30">
                  {pagination.total} personagens
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Posição
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Personagem
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Classe
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Level
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Pontos
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Jogador
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {ranking.map((character, index) => (
                      <motion.tr
                        key={character.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className={`hover:bg-white/5 transition-colors ${
                          character.user.id === user?.id ? 'bg-cyan-500/10' : ''
                        }`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{getRankIcon(index+1)}</span>
                            <span className="text-lg font-bold text-white">
                              #{index+1}
                            </span>
                            {character.user.id === user?.id && (
                              <span className="px-2 py-1 rounded-lg bg-cyan-500/20 text-cyan-400 text-xs font-medium border border-cyan-500/30">
                                Você
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getClassTypeIcon(character.class)}</span>
                            <span className="font-medium text-white">
                              {character.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getClassTypeCollor(character.class)}`}>
                            {character.class}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-white font-medium">
                          {character.level}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className="text-lg font-bold text-amber-400">
                            {character.totalPoints.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-400">
                          {character.user.name}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>
      ) : (
        !loading && (
          <EmptyState
            icon={Trophy}
            title="Ranking ainda vazio"
            description="Comece a participar de eventos e completar missões para aparecer no ranking!"
          />
        )
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Card variant="glass" className="mt-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Página {pagination.page} de {pagination.totalPages}
                <span className="text-gray-500 ml-2">({pagination.total} personagens)</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev || loading}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext || loading}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {loading && ranking.length > 0 && (
        <div className="flex justify-center mt-6">
          <LoadingSpinner text="Carregando..." />
        </div>
      )}
    </PageContainer>
  );
};

export default Ranking;
